import React, { useMemo, useState } from "react";
import { LinkIcon, TrashIcon, CopyIcon, CheckIcon, BriefIcon, DotsVerticalIcon, FolderIcon, DocumentIcon, StarIcon } from "@/components/icons";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import type { DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { ResourcePill } from "@/components/ui/ResourcePill";
import { copyToClipboard } from "@/utils/clipboard";
import { IdeaStatusBadge } from "./IdeaStatusBadge";
import { getStructuredPreview } from "@/components/ui/RichTextEditor";
import type { StructuredBlock } from "@/components/ui/RichTextEditor";
import { Idea, IdeaStatus, IDEA_BODY_SECTIONS } from "../types";
import { isEmptyBodyText } from "../ideaHelpers";
import { IDEA_STATUS_CONFIG } from "../ideaStatusConfig";
import { IDEA_CONTENT_TYPE_LABELS } from "../ideaContentTypeConfig";
import { useObjectiveTypes, resolveObjectiveLabel } from "../services/objectiveTypes";

function StructuredPreview({ blocks }: { blocks: StructuredBlock[] }) {
  return (
    <div className="text-xs text-gray-500 space-y-0.5 overflow-hidden">
      {blocks.map((block, i) => {
        switch (block.type) {
          case "heading":
            return (
              <p key={i} className="font-semibold text-gray-700 text-xs leading-snug truncate">
                {block.text}
              </p>
            );
          case "bullet":
            return (
              <p key={i} className="leading-snug truncate pl-2">
                <span className="text-gray-400 mr-1">&#x2022;</span>
                {block.text}
              </p>
            );
          case "numbered":
            return (
              <p key={i} className="leading-snug truncate pl-2">
                <span className="text-gray-400 mr-1">{block.level}.</span>
                {block.text}
              </p>
            );
          case "check":
            return (
              <p key={i} className="leading-snug truncate pl-2">
                <span className="text-gray-400 mr-1">&#x2610;</span>
                {block.text}
              </p>
            );
          default:
            return (
              <p key={i} className="leading-snug line-clamp-2">
                {block.text}
              </p>
            );
        }
      })}
    </div>
  );
}

interface IdeaPostItProps {
  idea: Idea;
  onClick?: (idea: Idea) => void;
  onStatusChange?: (idea: Idea, status: IdeaStatus) => void;
  onDuplicate?: (idea: Idea) => void;
  onDelete?: (idea: Idea) => void;
  onPostsClick?: (idea: Idea) => void;
  isUpdating?: boolean;
}

export const IdeaPostIt: React.FC<IdeaPostItProps> = ({
  idea,
  onClick,
  onStatusChange,
  onDuplicate,
  onDelete,
  onPostsClick,
  isUpdating = false,
}) => {
  const contentTypeLabel = idea.contentType ? IDEA_CONTENT_TYPE_LABELS[idea.contentType] : null;
  const { data: objectiveTypes = [] } = useObjectiveTypes();
  const objectiveLabel = resolveObjectiveLabel(idea.objectiveType, objectiveTypes);
  const [copiedId, setCopiedId] = useState(false);

  const handleCopyId = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(idea.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const actionItems = useMemo<DropdownMenuEntry[]>(() => {
    const items: DropdownMenuEntry[] = [];
    if (onDuplicate) {
      items.push({
        id: "duplicate",
        label: "Duplicate",
        icon: <CopyIcon className="w-4 h-4" />,
        onClick: () => onDuplicate(idea),
      });
    }
    if (onDelete) {
      if (items.length > 0) items.push({ id: "sep", separator: true });
      items.push({
        id: "delete",
        label: "Delete",
        icon: <TrashIcon className="w-4 h-4" />,
        onClick: () => onDelete(idea),
        variant: "danger",
      });
    }
    return items;
  }, [idea, onDuplicate, onDelete]);

  const collectionLink = idea.links?.find(l => l.targetType === "collection");
  const postLink = idea.links?.find(l => l.targetType === "post");

  return (
    <div
      className="group relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:scale-[1.01] transition-all duration-200 cursor-pointer h-[320px] flex flex-col overflow-hidden"
      onClick={() => onClick?.(idea)}
    >
      {/* Row 1: Status badge + actions menu */}
      <div className="flex items-center justify-between px-5 pt-5 pb-2">
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          <IdeaStatusBadge
            status={idea.status}
            onStatusChange={(status) => onStatusChange?.(idea, status)}
            disabled={isUpdating}
          />
          <button
            onClick={handleCopyId}
            className={`p-1 rounded-md transition-colors ${
              copiedId
                ? "bg-green-100 text-green-600"
                : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
            }`}
            title={copiedId ? "Copied!" : `Copy ID: ${idea.id}`}
          >
            {copiedId ? (
              <CheckIcon className="w-3.5 h-3.5" />
            ) : (
              <CopyIcon className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        {actionItems.length > 0 && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              items={actionItems}
              width="w-36"
              menuClassName="rounded-xl"
              trigger={
                <button className="p-1.5 rounded-full text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-colors">
                  <DotsVerticalIcon className="w-4 h-4" />
                </button>
              }
            />
          </div>
        )}
      </div>

      {/* Row 2: Content type + date meta line */}
      <div className="px-5 pb-2">
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
          {contentTypeLabel && <>{contentTypeLabel} &middot; </>}
          {new Date(idea.createdAt).toLocaleDateString("en-US", {
            month: "long",
            day: "2-digit",
            year: "numeric",
          })}
        </span>
      </div>

      {/* Row 3: Entity link chips */}
      {(collectionLink || postLink || idea.briefId) && (
        <div className="flex items-center gap-3 px-5 pb-3">
          {idea.briefId && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600">
              <BriefIcon className="w-3 h-3" />
              Brief
            </span>
          )}
          {collectionLink && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600">
              <FolderIcon className="w-3 h-3" />
              {collectionLink.targetName || "Related Collection"}
            </span>
          )}
          {postLink && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600">
              <DocumentIcon className="w-3 h-3" />
              View Original Post
            </span>
          )}
        </div>
      )}

      {/* Row 4: Large title */}
      <div className="px-5 pb-2">
        <h3 className="text-xl font-bold text-gray-900 leading-tight line-clamp-2">
          {idea.title}
        </h3>
        {objectiveLabel && (
          <span className="inline-flex items-center mt-1.5 px-2 py-0.5 text-[10px] font-medium bg-indigo-50 text-indigo-700 rounded-full">
            {objectiveLabel}
          </span>
        )}
      </div>

      {/* Row 5: Body preview */}
      <div className="px-5 flex-1 overflow-hidden">
        {(() => {
          const body = idea.body || {};
          const filledSections = IDEA_BODY_SECTIONS.filter(
            (s) => !isEmptyBodyText(body[s.key])
          );
          if (filledSections.length === 0) return null;
          const first = filledSections[0];
          const content = body[first.key] || "";
          const blocks = getStructuredPreview(content, 4);
          if (blocks.length === 0) return null;
          return <StructuredPreview blocks={blocks} />;
        })()}
      </div>

      {/* Client review surface — stars + comment from the client portal.
          Sits at the bottom of the tile (just above resource pills /
          footer) so admins scanning the grid can see at a glance which
          ideas the client liked. Independent of the status lifecycle. */}
      {(typeof idea.clientRating === "number" || idea.clientComment) && (
        <div className="px-5 pb-2 shrink-0">
          <div className="px-2.5 py-1.5 bg-amber-50/70 border border-amber-100 rounded-md">
            <div className="flex items-center gap-1.5">
              {typeof idea.clientRating === "number" && (
                <span className="inline-flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      className={`w-3 h-3 ${
                        star <= (idea.clientRating ?? 0)
                          ? "text-amber-500"
                          : "text-amber-200"
                      }`}
                      filled={star <= (idea.clientRating ?? 0)}
                    />
                  ))}
                </span>
              )}
              {idea.clientComment && (
                <span className="text-[10px] font-medium text-amber-700 uppercase tracking-[0.06em]">
                  · commento
                </span>
              )}
            </div>
            {idea.clientComment && (
              <p className="mt-1 text-[11px] text-amber-900 leading-snug line-clamp-2">
                “{idea.clientComment}”
              </p>
            )}
          </div>
        </div>
      )}

      {/* Row 6: Resource pills at bottom */}
      {idea.resources.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-5 pb-4 shrink-0" onClick={(e) => e.stopPropagation()}>
          {idea.resources.slice(0, 3).map((resource, i) => (
            <ResourcePill key={i} url={resource.url} note={resource.note} />
          ))}
          {idea.resources.length > 3 && (
            <span className="text-[10px] text-gray-400 font-medium">+{idea.resources.length - 3} more</span>
          )}
        </div>
      )}

      {/* Row 7: Subtle footer */}
      {!idea.resources.length && (
        <div className="px-5 pb-4 shrink-0">
          <div className="text-[10px] text-gray-300">
            {new Date(idea.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </div>
        </div>
      )}
    </div>
  );
};
